/* ===== Helpers numéricos/boolean ===== */
const cleanMoney = (v) => {
  if (v == null) return null;
  if (typeof v === "number") return v;
  let s = String(v).trim();
  if (!s) return null;
  s = s.replace(/[R$\s]/gi, "");
  if (/,/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
  s = s.replace(/[^0-9.-]/g, "");
  if (!s) return null;
  const num = Number(s);
  return Number.isFinite(num) ? num : null;
};
const cleanInt = (v) => {
  if (v == null) return null;
  if (typeof v === "number") return Math.trunc(v);
  const s = String(v).trim().replace(/[^\d-]/g, "");
  if (!s) return null;
  const num = Number(s);
  return Number.isFinite(num) ? Math.trunc(num) : null;
};
const bool01 = (v, def = 1) => {
  if (v == null || v === "") return def;
  if (typeof v === "number") return v === 1 ? 1 : 0;
  const s = String(v).trim().toLowerCase();
  if (["1", "sim", "ativo", "true", "on"].includes(s)) return 1;
  if (["0", "nao", "não", "inativo", "false", "off"].includes(s)) return 0;
  return def;
};

/* ===== Tokenização & headers ===== */
const tokenize = (line) => {
  if (line.includes(";")) return line.split(";").map((t) => t.trim());
  if (line.includes("|")) return line.split("|").map((t) => t.trim());
  if (/\t/.test(line)) return line.split("\t").map((t) => t.trim());
  return line.split(";").map((t) => t.trim()); // fallback
};
const normalizeHeader = (h) =>
  String(h || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/__+/g, "_");

const aliasKey = (k) => {
  switch (k) {
    case "nome_do_produto": return "nome";
    case "descricao":
    case "descricao_do_produto": return "detalhes";
    case "urls_imagens":
    case "urls_das_imagens":
    case "url_imagens":
    case "url_das_imagens":
    case "links_imagens":
    case "links_das_imagens":
    case "imagens":
    case "imagens_urls":
    case "urls":
    case "urls_das_imagens_do_produto_separadas_por_virgula":
      return "imagens_urls";
    default: return k;
  }
};

const allowedFieldsPaste = new Set([
  "sku","nome","preco","estoque","preco_comdesconto","status","tipo",
  "id_categoria","id_subcategoria","peso","largura","altura","comprimento",
  "detalhes","imagens_urls",
]);

const parseImageUrls = (v) => {
  if (v == null) return [];
  const s = String(v).trim();
  if (!s) return [];
  return s.split(/[,|;]+/g).map((x) => x.trim()).filter(Boolean);
};

export const parsePasteBlock = (text) => {
  const lines = (text || "")
    .split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  if (!lines.length) return { rowsValid: [], rowsInvalid: [] };

  const first = lines[0];
  const hasHeader =
    /(^|\W)nome($|\W)/i.test(first) ||
    /(^|\W)preco($|\W)/i.test(first) ||
    /(^|\W)estoque($|\W)/i.test(first) ||
    /(^|\W)sku($|\W)/i.test(first);

  let header = [];
  let dataLines = lines;
  if (hasHeader) {
    header = tokenize(first).map((h) => aliasKey(normalizeHeader(h)));
    dataLines = lines.slice(1);
  }

  const rows = [];

  for (const raw of dataLines) {
    let obj = {};

    if (raw.includes("=")) {
      const parts = raw.split(/;|\||\t|,/).map((p) => p.trim()).filter(Boolean);
      for (const part of parts) {
        const [k, ...rest] = part.split("=");
        const key = aliasKey(normalizeHeader(k || ""));
        const val = rest.join("=").trim();
        if (allowedFieldsPaste.has(key)) obj[key] = val;
      }
    } else if (hasHeader) {
      const cols = tokenize(raw);
      cols.forEach((val, idx) => {
        const key = header[idx];
        if (allowedFieldsPaste.has(key)) obj[key] = val;
      });
    } else {
      const [nome, preco, estoque, preco_comdesconto, status, tipo] = tokenize(raw);
      obj = { nome, preco, estoque, preco_comdesconto, status, tipo };
    }

    const imgArr = parseImageUrls(obj.imagens_urls ?? obj.imagens);
    const payload = {
      sku: obj.sku?.toString().trim() || null,
      nome: obj.nome?.trim() || "",
      preco: cleanMoney(obj.preco),
      estoque: cleanInt(obj.estoque),
      preco_comdesconto:
        obj.preco_comdesconto === "" || obj.preco_comdesconto == null
          ? null
          : cleanMoney(obj.preco_comdesconto),
      status: bool01(obj.status, 1),
      tipo: cleanInt(obj.tipo) || 1,
      id_categoria: obj.id_categoria ?? null,
      id_subcategoria: obj.id_subcategoria ?? null,
      peso: obj.peso == null || obj.peso === "" ? null : cleanMoney(obj.peso),
      largura: obj.largura == null || obj.largura === "" ? null : cleanMoney(obj.largura),
      altura: obj.altura == null || obj.altura === "" ? null : cleanMoney(obj.altura),
      comprimento: obj.comprimento == null || obj.comprimento === "" ? null : cleanMoney(obj.comprimento),
      detalhes: (obj.detalhes ?? obj.descricao ?? "").toString(),
      imagens: imgArr,
    };

    const hasRequired = payload.nome && payload.preco != null && payload.estoque != null;
    const discountOk = payload.preco_comdesconto == null || payload.preco_comdesconto <= payload.preco;

    rows.push({
      raw,
      payload,
      valid: !!(hasRequired && discountOk),
      reason: !hasRequired
        ? "Faltam obrigatórios (nome; preco; estoque)"
        : !discountOk
          ? "preco_comdesconto > preco"
          : null,
    });
  }

  const rowsValid = rows.filter((r) => r.valid).map((r) => r.payload);
  const rowsInvalid = rows.filter((r) => !r.valid).map((r) => ({ raw: r.raw, reason: r.reason }));
  return { rowsValid, rowsInvalid };
};
